/**
 * Document upload → reference extraction (client-side, zero dependencies).
 *
 * Two independent halves:
 *   1. `extractDocxText()` — pulls the plain text out of a .docx file. A .docx is a ZIP
 *      archive whose main body lives in `word/document.xml`; we parse the ZIP central
 *      directory by hand and inflate the entry with the browser-native
 *      `DecompressionStream`, so no zip/docx library is needed and nothing is uploaded.
 *   2. `extractReferences()` — given plain text (from a .docx, a .txt, or a paste),
 *      finds the reference-list section, splits it into entries, collects DOIs, scans
 *      the body for in-text citations, and cross-checks the two lists.
 *
 * Like `citation-check.ts`, this module is self-contained: it does NOT touch the
 * citation-formatting core, so it can never regress a golden snapshot. All findings are
 * advisory — heuristics tuned for precision over recall.
 */

import { splitEntries } from "@/lib/citation-check";

// --- ZIP / DOCX parsing -------------------------------------------------------

const EOCD_SIGNATURE = 0x06_05_4b_50; // "PK\x05\x06" little-endian
const CENTRAL_SIGNATURE = 0x02_01_4b_50; // "PK\x01\x02"
const LOCAL_SIGNATURE = 0x04_03_4b_50; // "PK\x03\x04"
// EOCD is 22 bytes + up to a 65535-byte comment.
const EOCD_SEARCH_SPAN = 22 + 0xff_ff;
const ZIP_METHOD_STORED = 0;
const ZIP_METHOD_DEFLATE = 8;
const DOCX_BODY_ENTRY = "word/document.xml";
const MAX_DOCX_BODY_BYTES = 20_000_000;

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

function findEndOfCentralDirectory(view: DataView): number {
  const start = Math.max(0, view.byteLength - EOCD_SEARCH_SPAN);
  for (let i = view.byteLength - 22; i >= start; i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      return i;
    }
  }
  throw new Error("Not a valid .docx file (ZIP directory not found).");
}

function readCentralDirectory(bytes: Uint8Array, view: DataView): ZipEntry[] {
  const eocd = findEndOfCentralDirectory(view);
  const entryCount = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder();
  const entries: ZipEntry[] = [];

  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(offset, true) !== CENTRAL_SIGNATURE) {
      break;
    }
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    entries.push({
      name: decoder.decode(
        bytes.subarray(offset + 46, offset + 46 + nameLength)
      ),
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      uncompressedSize: view.getUint32(offset + 24, true),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

function sliceEntryData(
  bytes: Uint8Array,
  view: DataView,
  entry: ZipEntry
): Uint8Array {
  const at = entry.localHeaderOffset;
  if (view.getUint32(at, true) !== LOCAL_SIGNATURE) {
    throw new Error("Not a valid .docx file (corrupt ZIP entry).");
  }
  const nameLength = view.getUint16(at + 26, true);
  const extraLength = view.getUint16(at + 28, true);
  const dataStart = at + 30 + nameLength + extraLength;
  return bytes.subarray(dataStart, dataStart + entry.compressedSize);
}

async function inflateRaw(
  data: Uint8Array,
  maxBytes: number
): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream("deflate-raw"));
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    total += value.byteLength;
    if (total > maxBytes) {
      await reader.cancel();
      throw new Error(
        "The .docx document body is too large to process safely."
      );
    }
    chunks.push(value);
  }
  const inflated = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    inflated.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return inflated;
}

// Tracked changes: text in <w:del> (and the moved-from half of a move) is gone from the
// document the author sees, so it must not be read. A self-closing <w:del/> inside a
// paragraph's properties only marks the paragraph break as deleted and holds no text.
const RE_TRACKED_DELETION =
  /<w:(del|moveFrom)\b(?![^>]*\/>)[^>]*>[\s\S]*?<\/w:\1>/g;
const RE_DELETED_TEXT = /<w:delText\b[^>]*>[\s\S]*?<\/w:delText>/g;
const RE_PARAGRAPH_END = /<\/w:p>/g;
const RE_LINE_BREAK = /<w:(?:br|cr)\b[^>]*\/?>/g;
const RE_TAB = /<w:tab\b[^>]*\/?>/g;
const RE_XML_TAG = /<[^>]+>/g;
const RE_NUMERIC_ENTITY = /&#(x?[0-9a-fA-F]+);/g;
const RE_BLANK_RUNS = /\n{3,}/g;

function decodeXmlEntities(text: string): string {
  return text
    .replace(RE_NUMERIC_ENTITY, (_match, code: string) => {
      const value = code.startsWith("x")
        ? Number.parseInt(code.slice(1), 16)
        : Number.parseInt(code, 10);
      return Number.isNaN(value) ? "" : String.fromCodePoint(value);
    })
    .replaceAll("&quot;", '"')
    .replaceAll("&apos;", "'")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&amp;", "&");
}

/** Convert the WordprocessingML body to plain text, one paragraph per line. */
export function docxXmlToText(xml: string): string {
  const withBreaks = xml
    .replace(RE_TRACKED_DELETION, "")
    .replace(RE_DELETED_TEXT, "")
    .replace(RE_PARAGRAPH_END, "\n")
    .replace(RE_LINE_BREAK, "\n")
    .replace(RE_TAB, "\t");
  return decodeXmlEntities(withBreaks.replace(RE_XML_TAG, ""))
    .replace(RE_BLANK_RUNS, "\n\n")
    .trim();
}

/**
 * Extract the plain text of a .docx file entirely in the browser. Throws a
 * user-presentable Error for anything that is not a readable .docx.
 */
export async function extractDocxText(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  if (bytes.byteLength < 22) {
    throw new Error("Not a valid .docx file (file is too small).");
  }
  const view = new DataView(buffer);
  const entries = readCentralDirectory(bytes, view);
  const body = entries.find((entry) => entry.name === DOCX_BODY_ENTRY);
  if (!body) {
    throw new Error(
      "No document body found — is this a Word (.docx) file? Older .doc files are not supported; save as .docx and try again."
    );
  }
  if (body.uncompressedSize > MAX_DOCX_BODY_BYTES) {
    throw new Error("The .docx document body is too large to process safely.");
  }
  const raw = sliceEntryData(bytes, view, body);
  let inflated: Uint8Array;
  if (body.method === ZIP_METHOD_STORED) {
    if (raw.byteLength > MAX_DOCX_BODY_BYTES) {
      throw new Error(
        "The .docx document body is too large to process safely."
      );
    }
    inflated = raw;
  } else if (body.method === ZIP_METHOD_DEFLATE) {
    inflated = await inflateRaw(raw, MAX_DOCX_BODY_BYTES);
  } else {
    throw new Error("Unsupported .docx compression method.");
  }
  return docxXmlToText(new TextDecoder().decode(inflated));
}

// --- PDF DOI extraction ---------------------------------------------------------
//
// Full PDF text extraction is deliberately out of scope (font CMaps make it a
// project of its own), but DOIs are reliably recoverable: publishers embed them in
// the (uncompressed) XMP/Info metadata and in Flate-compressed content streams,
// which the browser can inflate natively. A found DOI feeds the existing DOI
// citation path, which returns clean metadata anyway — better than scraping the
// PDF's text layer.

const PDF_HEADER = "%PDF";
const RE_PDF_STREAM = /stream\r?\n/g;
const PDF_ENDSTREAM = "endstream";
// Zlib-wrapped deflate: 0x78 first byte (CMF: 32K window, deflate method).
const ZLIB_FIRST_BYTE = 0x78;
const MAX_PDF_STREAMS = 200;
const MAX_STREAM_BYTES = 5_000_000;
const RE_DOI_GLOBAL = /\b10\.\d{4,9}\/[^\s"'<>)\]}]+/g;
const RE_DOI_TRAILING = /[.,;]+$/;

async function inflateZlib(data: Uint8Array): Promise<Uint8Array | null> {
  try {
    const stream = new Blob([data as BlobPart])
      .stream()
      .pipeThrough(new DecompressionStream("deflate"));
    const buffer = await new Response(stream).arrayBuffer();
    return new Uint8Array(buffer);
  } catch {
    return null;
  }
}

function collectDois(text: string, seen: Set<string>, out: string[]): void {
  for (const match of text.matchAll(RE_DOI_GLOBAL)) {
    const doi = match[0].replace(RE_DOI_TRAILING, "");
    const key = doi.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(doi);
    }
  }
}

/**
 * Extract every DOI discoverable in a PDF, in order of first appearance:
 * first from the raw bytes (metadata, uncompressed objects), then from
 * Flate-compressed streams inflated in the browser. Throws for non-PDF input.
 */
export async function extractPdfDois(buffer: ArrayBuffer): Promise<string[]> {
  const bytes = new Uint8Array(buffer);
  // latin1 maps every byte to a code point, so offsets survive the decode.
  const raw = new TextDecoder("latin1").decode(bytes);
  if (!raw.startsWith(PDF_HEADER)) {
    throw new Error("Not a PDF file — upload a file that starts with %PDF.");
  }

  const seen = new Set<string>();
  const dois: string[] = [];
  collectDois(raw, seen, dois);

  // Inflate compressed streams and scan those too.
  let streamCount = 0;
  for (const match of raw.matchAll(RE_PDF_STREAM)) {
    if (streamCount >= MAX_PDF_STREAMS) {
      break;
    }
    const start = match.index + match[0].length;
    const end = raw.indexOf(PDF_ENDSTREAM, start);
    if (end === -1 || end - start > MAX_STREAM_BYTES) {
      continue;
    }
    if (bytes[start] !== ZLIB_FIRST_BYTE) {
      continue;
    }
    streamCount += 1;
    // The EOL before `endstream` is not part of the stream data — trailing bytes
    // make strict zlib decoders reject the whole stream.
    let dataEnd = end;
    while (
      dataEnd > start &&
      (bytes[dataEnd - 1] === 0x0a ||
        bytes[dataEnd - 1] === 0x0d ||
        bytes[dataEnd - 1] === 0x20)
    ) {
      dataEnd -= 1;
    }
    const inflated = await inflateZlib(bytes.subarray(start, dataEnd));
    if (inflated) {
      collectDois(new TextDecoder("latin1").decode(inflated), seen, dois);
    }
  }

  return dois;
}

// --- Reference-list extraction ------------------------------------------------

export interface InTextCitation {
  /** The citation as it appears in the text, e.g. "(Smith, 2020)" or "Smith (2020)". */
  raw: string;
  /** First author surname, when the citation is author–date. */
  surname: string;
  /** The cited year, e.g. "2020", "2020a", or "n.d.". */
  year: string;
}

export interface ReferenceExtraction {
  /** The section heading that was matched ("References", "Works Cited", …), if any. */
  headingFound: string | null;
  /** The reference-list entries, one string per source. */
  entries: string[];
  /** Unique DOIs found anywhere in the document, in order of first appearance. */
  dois: string[];
  /** Unique author–date in-text citations found in the body text. */
  inTextCitations: InTextCitation[];
  /** In-text citations with no matching reference entry (surname + year). */
  missingFromList: InTextCitation[];
  /** Reference entries that are never cited in the body text. */
  neverCited: string[];
}

// English headings first, then the usual reference-list headings in the site's other
// languages, so a paper written in any of them is found as well.
const RE_SECTION_HEADING =
  /^(references|reference list|bibliography|works cited|works consulted|literature cited|sources|sources cited|referencias(?: bibliográficas)?|bibliografía|obras citadas|literaturverzeichnis|literatur|quellenverzeichnis|quellen|références(?: bibliographiques)?|bibliographie|ouvrages cités|riferimenti(?: bibliografici)?|bibliografia|referências(?: bibliográficas)?|literatuurlijst|bronnenlijst|referenties|bibliografie|literatura|źródła|список литературы|литература|библиография|источники|kaynakça|kaynaklar|daftar pustaka|referensi|tài liệu tham khảo|संदर्भ(?: सूची)?|ग्रंथसूची|参考文献|引用文献|참고\s?문헌|المراجع|قائمة المراجع|المصادر)\s*[:：]?\s*$/i;
const RE_SECTION_END =
  /^(appendix\b.*|appendices|tables|figures|notes|endnotes|footnotes|acknowledgm?ents|supplementary materials?|about the authors?)\s*:?\s*$/i;
const RE_ANY_YEAR = /\((\d{4}[a-z]?|n\.d\.)\)/;
const RE_DOI = /\b10\.\d{4,9}\/[^\s"'<>]+/g;
const RE_DOI_TRAILING_PUNCT = /[.,;)\]]+$/;
const RE_LOOKS_LIKE_REFERENCE = /\((\d{4}[a-z]?|n\.d\.)\)|\b10\.\d{4,9}\//;
const RE_ENTRY_SURNAME = /^([A-Za-zÀ-ɏ'’-]+)/;
// "(Smith, 2020)", "(Smith & Jones, 2020; Doe, 2019)" — any parenthetical containing a year.
const RE_PARENTHETICAL = /\(([^()]*(?:\d{4}[a-z]?|n\.d\.)[^()]*)\)/g;
// "Smith (2020)", "Smith et al. (2020)", "Smith and Jones (2020)"
const RE_NARRATIVE =
  /\b([A-Z][A-Za-zÀ-ɏ'’-]+)(?:\s+et\s+al\.?|\s+(?:and|&)\s+[A-Z][A-Za-zÀ-ɏ'’-]+)?\s+\((\d{4}[a-z]?|n\.d\.)\)/g;
const RE_CITATION_PART_SPLIT = /;/;
const RE_PART_YEAR = /\b(\d{4}[a-z]?)\b|\bn\.d\./;
const RE_PART_SURNAME = /([A-Z][A-Za-zÀ-ɏ'’-]+)/;
const RE_ET_AL_PREFIX = /^et$/i;
const RE_YEAR_LETTER_SUFFIX = /[a-z]$/;
const MIN_ENTRY_LENGTH = 15;

function findReferenceSection(lines: string[]): {
  heading: string | null;
  block: string[];
} {
  let headingIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (RE_SECTION_HEADING.test(lines[i].trim())) {
      headingIndex = i;
    }
  }
  if (headingIndex === -1) {
    return { heading: null, block: [] };
  }
  const block: string[] = [];
  for (let i = headingIndex + 1; i < lines.length; i++) {
    if (RE_SECTION_END.test(lines[i].trim())) {
      break;
    }
    block.push(lines[i]);
  }
  return { heading: lines[headingIndex].trim(), block };
}

function extractEntries(text: string): {
  heading: string | null;
  entries: string[];
  bodyText: string;
} {
  const lines = text.split("\n");
  const { heading, block } = findReferenceSection(lines);

  if (heading) {
    const entries = splitEntries(block.join("\n")).filter(
      (entry) => entry.length >= MIN_ENTRY_LENGTH
    );
    const headingAt = text.lastIndexOf(heading);
    return { heading, entries, bodyText: text.slice(0, headingAt) };
  }

  // No heading: if the text overwhelmingly looks like a pasted reference list,
  // treat the whole thing as the list (and there is no body to cross-check).
  const candidates = splitEntries(text);
  const referencey = candidates.filter((line) =>
    RE_LOOKS_LIKE_REFERENCE.test(line)
  );
  if (candidates.length >= 2 && referencey.length / candidates.length >= 0.6) {
    return {
      heading: null,
      entries: candidates.filter((entry) => entry.length >= MIN_ENTRY_LENGTH),
      bodyText: "",
    };
  }
  return { heading: null, entries: [], bodyText: text };
}

function extractDois(text: string): string[] {
  const seen = new Set<string>();
  const dois: string[] = [];
  for (const match of text.matchAll(RE_DOI)) {
    const doi = match[0].replace(RE_DOI_TRAILING_PUNCT, "");
    if (!seen.has(doi.toLowerCase())) {
      seen.add(doi.toLowerCase());
      dois.push(doi);
    }
  }
  return dois;
}

function parseParentheticalPart(part: string): InTextCitation | null {
  const yearMatch = RE_PART_YEAR.exec(part);
  if (!yearMatch) {
    return null;
  }
  const beforeYear = part.slice(0, yearMatch.index);
  const surnameMatch = RE_PART_SURNAME.exec(beforeYear);
  if (!surnameMatch || RE_ET_AL_PREFIX.test(surnameMatch[1])) {
    return null;
  }
  const year = yearMatch[1] ?? "n.d.";
  return {
    raw: `(${part.trim()})`,
    surname: surnameMatch[1],
    year,
  };
}

function citationKey(surname: string, year: string): string {
  // Compare on the base year so "2020a" matches an entry dated "(2020a)" or "(2020)".
  return `${surname.toLowerCase()}|${year.replace(RE_YEAR_LETTER_SUFFIX, "")}`;
}

function collectInTextCitations(bodyText: string): InTextCitation[] {
  const found = new Map<string, InTextCitation>();

  for (const match of bodyText.matchAll(RE_PARENTHETICAL)) {
    for (const part of match[1].split(RE_CITATION_PART_SPLIT)) {
      const citation = parseParentheticalPart(part);
      if (citation) {
        const key = citationKey(citation.surname, citation.year);
        if (!found.has(key)) {
          found.set(key, citation);
        }
      }
    }
  }

  for (const match of bodyText.matchAll(RE_NARRATIVE)) {
    const citation: InTextCitation = {
      raw: match[0],
      surname: match[1],
      year: match[2],
    };
    const key = citationKey(citation.surname, citation.year);
    if (!found.has(key)) {
      found.set(key, citation);
    }
  }

  return [...found.values()];
}

function entryKey(entry: string): string | null {
  const surnameMatch = RE_ENTRY_SURNAME.exec(entry);
  const yearMatch = RE_ANY_YEAR.exec(entry);
  if (!(surnameMatch && yearMatch)) {
    return null;
  }
  return citationKey(surnameMatch[1], yearMatch[1]);
}

/**
 * Extract the reference list, DOIs, and in-text citations from a document's plain
 * text, and cross-check the in-text citations against the list. The cross-check only
 * runs when a labelled reference section was found (so a bare pasted list is never
 * flagged as "never cited").
 */
export function extractReferences(text: string): ReferenceExtraction {
  const normalized = text.replaceAll("\r\n", "\n");
  const { heading, entries, bodyText } = extractEntries(normalized);
  const inTextCitations = collectInTextCitations(bodyText);

  let missingFromList: InTextCitation[] = [];
  let neverCited: string[] = [];
  if (heading && entries.length > 0) {
    const entryKeys = new Set<string>();
    for (const entry of entries) {
      const key = entryKey(entry);
      if (key) {
        entryKeys.add(key);
      }
    }
    missingFromList = inTextCitations.filter(
      (citation) => !entryKeys.has(citationKey(citation.surname, citation.year))
    );
    const citedKeys = new Set(
      inTextCitations.map((citation) =>
        citationKey(citation.surname, citation.year)
      )
    );
    neverCited = entries.filter((entry) => {
      const key = entryKey(entry);
      return key !== null && !citedKeys.has(key);
    });
  }

  return {
    headingFound: heading,
    entries,
    dois: extractDois(normalized),
    inTextCitations,
    missingFromList,
    neverCited,
  };
}
